import React, { useMemo, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  type FiltersDefinition,
  GroupingDefinition,
  type ItemActionsDefinition,
  type NavigationFiltersDefinition,
  OneDataCollection,
  PaginatedFetchOptions,
  PaginatedResponse,
  type SortingsDefinition,
  type SummariesDefinition,
  useDataSource,
} from '@factorialco/factorial-one-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

import {
  DEFAULT_CATEGORIES,
  type GroupedData,
  type NodeChildren,
  TreeNode,
  getChildTypeText,
  normalizeNodeType,
  parseSalaryRangeFromAttributes,
} from 'modules/job_catalog/lib/nodesUtils'

import EmptyState from '../EmptyState'

interface Props {
  currentNode: TreeNode
  childrenInformation: NodeChildren[]
}

const buildDataFromChildren = (
  childrenInformation: NodeChildren[],
  setTotalItems?: (total: number) => void
): GroupedData[] => {
  setTotalItems?.(childrenInformation.length)

  return childrenInformation.map((child) => ({
    name: child.name,
    uuid: child.id.toString(),
    salary: parseSalaryRangeFromAttributes(child.attributesJson),
  }))
}

const OverviewAttributes = ({ currentNode, childrenInformation }: Props) => {
  const [totalItems, setTotalItems] = useState(0)

  const data = useMemo(
    () => buildDataFromChildren(childrenInformation, setTotalItems),
    [childrenInformation]
  )

  const nodeType = normalizeNodeType(currentNode.type)

  const columns = useMemo(() => {
    const baseColumns = []

    const nameColumnLabel = getChildTypeText(currentNode.node_uuid) || ''

    baseColumns.push({
      label: nameColumnLabel,
      render: (i: GroupedData) => ({
        type: 'text' as const,
        value: (i.name || '').length > 30 ? `${(i.name || '').slice(0, 30)}...` : i.name || '',
      }),
    })

    baseColumns.push({
      label: i18n.t('job_catalog.nodes.salary'),
      render: (i: GroupedData) => ({
        type: 'text' as const,
        value: i.salary || '-',
      }),
    })

    return baseColumns
  }, [nodeType])

  const source = useDataSource<
    GroupedData,
    FiltersDefinition<string>,
    SortingsDefinition,
    SummariesDefinition,
    ItemActionsDefinition<GroupedData>,
    NavigationFiltersDefinition,
    GroupingDefinition<GroupedData>
  >({
    dataAdapter: {
      fetchData: ({
        pagination,
      }: PaginatedFetchOptions<FiltersDefinition<string>, NavigationFiltersDefinition>): Promise<
        PaginatedResponse<GroupedData>
      > => {
        const currentPage = pagination?.currentPage || 1
        const perPage = 10
        const start = (currentPage - 1) * perPage
        const end = start + perPage
        const paginatedData = data.slice(start, end)

        return Promise.resolve({
          type: 'pages',
          records: paginatedData,
          total: data.length,
          currentPage,
          perPage,
          pagesCount: Math.ceil(data.length / perPage),
        })
      },
      perPage: 10,
      paginationType: 'pages',
    },
  })

  if (!data || data.length === 0) {
    return <EmptyState category={DEFAULT_CATEGORIES.OVERVIEW} />
  }

  return (
    <>
      <Box paddingX="s24">
        <Text.Header3 color="grey800">
          {getChildTypeText(currentNode.node_uuid, totalItems)}
        </Text.Header3>
      </Box>
      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: { columns, frozenColumns: 0 },
          },
        ]}
      />
    </>
  )
}

export default OverviewAttributes
